import React, { useCallback, useEffect, useRef, useState } from "react";
import "./EditorLeftMenuResizeControls.css";
import ResizeControlMenu, {
  ResizePresetSearch,
} from "./ResizeControlMenu";
import ResizeModeCards from "./ResizeModeCards";
import ResizeCustomSizeFlyout from "./ResizeCustomSizeFlyout";
import {
  addRecentResizePreset,
  buildCustomResizePreset,
  isCustomResizePreset,
} from "./customResizePreset";
import { parsePresetPixelSize } from "../../../../../utils/getTransform";

const EditorLeftMenuResizeControls = ({
  onResizeSelect = () => {},
  onResizeModeChange = () => {},
  selectedPresetId = null,
  selectedPreset = null,
  resizeMode = "fit",
  previewImageUrl = null,
}) => {
  const panelRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const [catalogCustomPreset, setCatalogCustomPreset] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!previewImageUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setNaturalSize({
          width: img.naturalWidth || 0,
          height: img.naturalHeight || 0,
        });
      }
    };
    img.onerror = () => {
      if (!cancelled) setNaturalSize({ width: 0, height: 0 });
    };
    img.src = previewImageUrl;
    return () => {
      cancelled = true;
    };
  }, [previewImageUrl]);

  const closeFlyout = useCallback(() => {
    setFlyoutOpen(false);
    setFlyoutAnchor(null);
  }, []);

  const handleCustomPresetActivate = useCallback(({ preset, anchorEl }) => {
    setCatalogCustomPreset(preset || null);
    setFlyoutAnchor(anchorEl || null);
    setFlyoutOpen(true);
  }, []);

  const handleCustomContinue = useCallback(
    ({ width, height }) => {
      const preset = buildCustomResizePreset(
        width,
        height,
        catalogCustomPreset
      );
      if (!preset) return;
      addRecentResizePreset(preset);
      onResizeSelect(preset);
      closeFlyout();
    },
    [catalogCustomPreset, closeFlyout, onResizeSelect]
  );

  const handlePresetSelect = useCallback(
    (preset) => {
      closeFlyout();
      onResizeSelect(preset);
    },
    [closeFlyout, onResizeSelect]
  );

  const selectedParsed = parsePresetPixelSize(selectedPreset);
  const initialWidth =
    isCustomResizePreset(selectedPreset) && selectedParsed
      ? selectedParsed.width
      : naturalSize.width || null;
  const initialHeight =
    isCustomResizePreset(selectedPreset) && selectedParsed
      ? selectedParsed.height
      : naturalSize.height || null;

  return (
    <div className="resize-controls-container" ref={panelRef}>
      <div className="resize-controls-sticky">
        <h1>Resize</h1>
        {selectedPreset ? (
          <div className="resize-controls-modes">
            <ResizeModeCards
              resizeMode={resizeMode}
              onModeChange={onResizeModeChange}
              imageUrl={previewImageUrl}
              preset={selectedPreset}
            />
          </div>
        ) : null}
        <ResizePresetSearch value={searchTerm} onChange={setSearchTerm} />
      </div>
      <ResizeControlMenu
        onResizeSelect={handlePresetSelect}
        onCustomPresetActivate={handleCustomPresetActivate}
        selectedPresetId={selectedPresetId}
        selectedPreset={selectedPreset}
        customFlyoutOpen={flyoutOpen}
        searchTerm={searchTerm}
        hideSearch
      />
      <ResizeCustomSizeFlyout
        open={flyoutOpen}
        anchorEl={flyoutAnchor}
        panelRef={panelRef}
        naturalWidth={naturalSize.width}
        naturalHeight={naturalSize.height}
        initialWidth={initialWidth}
        initialHeight={initialHeight}
        onContinue={handleCustomContinue}
        onClose={closeFlyout}
      />
    </div>
  );
};

export default EditorLeftMenuResizeControls;
